import type { ListBuilderConfiguration } from '../types';

/**
 * Driver list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const DRIVER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-drivers',
			name: 'All Drivers',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			columns: [
				{
					id: 'driver-name',
					headers: [
						{
							id: 'driver-name-header',
							text: 'Driver Name',
						},
					],
					fields: [
						{
							id: 'driver-name-field',
							dataConfig: {
								type: 'template',
								template: '{driver.firstName} {driver.lastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
							link: {
								type: 'object',
								enabled: true,
							},
						},
					],
					sortable: true,
				},
				{
					id: 'status',
					headers: [
						{
							id: 'status-header',
							text: 'Status',
						},
					],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'status',
									objectTable: 'driver',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: [
									{
										value: 'active',
										label: 'Active',
										color: {
											bg: '#dcfce7',
											text: '#166534',
										},
									},
									{
										value: 'inactive',
										label: 'Inactive',
										color: {
											bg: '#e5e7eb',
											text: '#4b5563',
										},
									},
									{
										value: 'on-route',
										label: 'On Route',
										color: {
											bg: '#fef3c7',
											text: '#92400e',
										},
									},
								],
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					width: 120,
				},
				{
					id: 'assigned-routes',
					headers: [
						{
							id: 'assigned-routes-header',
							text: 'Assigned Routes',
						},
					],
					fields: [
						{
							id: 'assigned-routes-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'assignedRoutesCount',
									objectTable: 'driver',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 140,
				},
				{
					id: 'total-deliveries',
					headers: [
						{
							id: 'total-deliveries-header',
							text: 'Total Deliveries',
						},
					],
					fields: [
						{
							id: 'total-deliveries-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'totalDeliveries',
									objectTable: 'driver',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 140,
				},
				{
					id: 'on-time-percentage',
					headers: [
						{
							id: 'on-time-percentage-header',
							text: 'On-Time %',
						},
					],
					fields: [
						{
							id: 'on-time-percentage-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'onTimePercentage',
									objectTable: 'driver',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								// `decimalPlaces: 0` keeps the cell tidy as "75%"
								// rather than "75.00%" (the formatter's default).
								// The detail card uses the same precision, so
								// list + detail now match.
								formatOptions: {
									decimalPlaces: 0,
								},
							},
							// D008 fix (2026-05-26 E2E pass): was 'text' which
							// rendered "0" — inconsistent with the driver detail
							// card showing "0%". Using 'percent' lets the
							// list-builder's number formatter append the % suffix
							// (see csvFieldExtractor.ts).
							displayStyle: 'percent',
						},
					],
					sortable: true,
					width: 120,
				},
				{
					// D007 (2026-05-26): "Recent Trips" = trips driven in
					// the last 14 days. Surfaces driver recency on the
					// list view; full detail tab adds tooltips/context.
					id: 'recent-trips-count',
					headers: [
						{
							id: 'recent-trips-count-header',
							text: 'Trips (14d)',
						},
					],
					fields: [
						{
							id: 'recent-trips-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'recentTripsCount',
									objectTable: 'driver',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 120,
				},
			],
			defaultSortId: 'driver-name',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: ['driverSearch', 'driverStatus'],
		defaultFilters: {},
	},
	// The list above declares `defaultSortId: 'driver-name'`, so the
	// sort resolver expects a matching tableSort entry. Without this,
	// ListBuilder logs "defaultSortId 'driver-name' did not resolve to
	// any sort fields" and falls back to a usable sort. Mirrors the
	// 2026-05-26 fix on the vehicles list (task #45). Driver name on
	// the column is a `{driver.firstName} {driver.lastName}` template,
	// so the canonical sort is lastName→firstName (directory ordering).
	tableSorts: [
		{
			id: 'driver-name',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'lastName', objectTable: 'driver' },
				{ direction: 'ASC', fieldName: 'firstName', objectTable: 'driver' },
			],
			description: 'Sort drivers alphabetically by last name, then first',
		},
		{
			id: 'driver-name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'lastName', objectTable: 'driver' },
				{ direction: 'DESC', fieldName: 'firstName', objectTable: 'driver' },
			],
			description:
				'Sort drivers reverse-alphabetically by last name, then first',
		},
		{
			id: 'status-asc',
			name: 'Status (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'status', objectTable: 'driver' },
				{ direction: 'ASC', fieldName: 'lastName', objectTable: 'driver' },
			],
			description: 'Group drivers by status, then alphabetical by last name',
		},
	],
	objects: [
		{
			baseUrl: '/distribution/drivers/:id',
			objectLabel: 'Driver',
			objectTable: 'driver',
			selectedFields: [
				{
					fieldName: '_id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'firstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'lastName',
					fieldLabel: 'Last Name',
				},
				{
					fieldName: 'phoneNumber',
					fieldLabel: 'Phone',
				},
				{
					fieldName: 'email',
					fieldLabel: 'Email',
				},
				{
					fieldName: 'status',
					fieldLabel: 'Status',
				},
				{
					fieldName: 'assignedRoutesCount',
					fieldLabel: 'Assigned Routes',
				},
				{
					fieldName: 'totalDeliveries',
					fieldLabel: 'Total Deliveries',
				},
				{
					fieldName: 'onTimePercentage',
					fieldLabel: 'On-Time Percentage',
				},
				{
					// D008 (2026-05-26): denominator for on-time%. Number
					// of stops that had an ETA to evaluate against.
					fieldName: 'onTimeEligibleDeliveries',
					fieldLabel: 'On-Time Eligible Deliveries',
				},
				{
					// D007 (2026-05-26): 14-day trip count (recency).
					fieldName: 'recentTripsCount',
					fieldLabel: 'Trips (Last 14 Days)',
				},
				{
					// D008 (2026-05-26): avg late-departure offset.
					fieldName: 'averageStartDelayMinutes',
					fieldLabel: 'Avg Start Delay (min)',
				},
				{
					fieldName: 'createdDate',
					fieldLabel: 'Created Date',
				},
				{
					fieldName: 'modifiedDate',
					fieldLabel: 'Modified Date',
				},
			],
		},
	],
};
